import { useEffect, useState } from "react";

import {
  type GroupMemberDTO,
  type LadderDTO,
  type MatchSummaryDTO,
  deleteMatch,
  getGroupLadder,
  getRecentMatches,
} from "../api";
import { LadderGuide } from "./LadderGuide";
import { RecordResultModal } from "./RecordResultModal";

// A group's ladder: ranked positions for the selected format. Positions only —
// the underlying rating is hidden by design. Admins can record results, which
// reorders the ladder.
export function LadderSection({
  groupId,
  members,
  isAdmin,
  defaultFormat,
}: {
  groupId: string;
  members: GroupMemberDTO[];
  isAdmin: boolean;
  defaultFormat: "singles" | "doubles";
}) {
  const [format, setFormat] = useState<"singles" | "doubles">(defaultFormat);
  const [ladder, setLadder] = useState<LadderDTO | null>(null);
  const [matches, setMatches] = useState<MatchSummaryDTO[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLadder(null);
    setMatches([]);
    setError(null);
    getGroupLadder(groupId, format)
      .then((d) => !cancelled && setLadder(d))
      .catch((e) => !cancelled && setError(String(e?.message ?? e)));
    if (isAdmin) {
      getRecentMatches(groupId, format)
        .then((d) => !cancelled && setMatches(d.matches))
        .catch(() => undefined);
    }
    return () => {
      cancelled = true;
    };
  }, [groupId, format, isAdmin]);

  function refresh() {
    getGroupLadder(groupId, format)
      .then(setLadder)
      .catch((e) => setError(String(e?.message ?? e)));
    if (isAdmin) {
      getRecentMatches(groupId, format)
        .then((d) => setMatches(d.matches))
        .catch(() => undefined);
    }
  }

  async function undo(matchId: string) {
    setBusyId(matchId);
    setError(null);
    try {
      const next = await deleteMatch(groupId, matchId);
      setLadder(next);
      setMatches((ms) => ms.filter((m) => m.id !== matchId));
    } catch (e) {
      setError(String((e as Error)?.message ?? e));
    } finally {
      setBusyId(null);
    }
  }

  const entries = ladder?.entries ?? [];

  return (
    <section className="mb-10">
      <div className="flex flex-wrap items-center justify-between mb-3 gap-x-3 gap-y-2">
        {/* Left: heading + the format view-switch. */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-baseline gap-2">
            <h2 className="font-display font-bold text-2xl">Ladder</h2>
            <button
              onClick={() => setGuideOpen(true)}
              className="font-mono text-[10px] uppercase tracking-wider text-ink/45 hover:text-ink underline decoration-accent-dim decoration-2 underline-offset-2 cs-tap"
            >
              How it works
            </button>
          </div>
          {/* Format is a view switch — a segmented toggle, not standalone
              buttons that read like CTAs. */}
          <div className="inline-flex rounded-md border border-ink/20 overflow-hidden">
            {(["singles", "doubles"] as const).map((f, i) => (
              <button
                key={f}
                onClick={() => setFormat(f)}
                aria-pressed={format === f}
                className={
                  "px-3 py-1.5 font-display font-bold text-[11px] uppercase tracking-wider cs-tap transition-colors " +
                  (i > 0 ? "border-l border-ink/20 " : "") +
                  (format === f
                    ? "bg-ink text-bg"
                    : "bg-bg text-ink/55 hover:text-ink hover:bg-bg-2")
                }
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        {/* Right: the one action. */}
        {isAdmin && (
          <button
            onClick={() => setRecording(true)}
            className="px-3 py-1.5 bg-accent text-ink font-display font-bold text-[11px] uppercase tracking-wider cs-tap"
          >
            Record result
          </button>
        )}
      </div>

      {error && (
        <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </div>
      )}

      {ladder == null && !error ? (
        <div className="text-ink/55 text-sm py-6 text-center">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="border border-ink/15 rounded-md px-4 py-6 text-center text-ink/55 text-sm">
          No {format} results recorded yet.
          {isAdmin && " Record a result to start the ladder."}
        </div>
      ) : (
        <ol className="flex flex-col gap-1.5">
          {entries.map((e) => (
            <li
              key={e.user_id}
              className={
                "flex items-center gap-3 px-3 py-2.5 border rounded-md " +
                (e.is_you ? "border-ink/40 bg-bg-2" : "border-ink/15")
              }
            >
              <span className="w-7 text-right font-mono text-sm text-ink/45 tabular-nums shrink-0">
                {e.position}
              </span>
              <span className="w-8 h-8 rounded-full flex items-center justify-center font-display font-bold text-xs shrink-0 bg-bg-3 text-ink">
                {e.initials}
              </span>
              <span className="flex-1 truncate text-sm">
                {e.name}
                {e.is_you && <span className="text-ink/40"> (you)</span>}
              </span>
              <span className="font-mono text-[11px] text-ink/45 shrink-0">
                {e.matches_played} {e.matches_played === 1 ? "match" : "matches"}
              </span>
            </li>
          ))}
        </ol>
      )}

      {/* Recent results — admin can undo any of them (ratings recompute). */}
      {isAdmin && matches.length > 0 && (
        <div className="mt-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/45 mb-2">
            Recent results
          </div>
          <ul className="flex flex-col gap-1">
            {matches.map((m) => {
              const a = m.team_a.join(" & ");
              const b = m.team_b.join(" & ");
              const winA = m.winner === "a";
              return (
                <li
                  key={m.id}
                  className="flex items-center gap-2 px-3 py-2 border border-ink/10 rounded-md text-sm"
                >
                  <span className="flex-1 min-w-0">
                    <span className="block truncate">
                      <span className={winA ? "font-bold" : "text-ink/55"}>{a}</span>
                      <span className="text-ink/35"> vs </span>
                      <span className={!winA ? "font-bold" : "text-ink/55"}>{b}</span>
                      {m.score && <span className="text-ink/45 font-mono text-xs"> · {m.score}</span>}
                    </span>
                    <span className="block truncate font-mono text-[10px] uppercase tracking-wider text-ink/40">
                      {m.session_label ? `from ${m.session_label}` : "pickup"}
                    </span>
                  </span>
                  <button
                    onClick={() => void undo(m.id)}
                    disabled={busyId === m.id}
                    title="Undo this result"
                    className="shrink-0 font-mono text-[10px] uppercase tracking-wider border border-ink/20 hover:border-[#C84B31] hover:text-[#C84B31] px-2.5 py-1 cs-tap disabled:opacity-40"
                  >
                    {busyId === m.id ? "…" : "Undo"}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {recording && (
        <RecordResultModal
          groupId={groupId}
          players={members}
          defaultFormat={format}
          onClose={(recorded) => {
            setRecording(false);
            if (recorded) refresh();
          }}
        />
      )}

      {guideOpen && <LadderGuide onClose={() => setGuideOpen(false)} />}
    </section>
  );
}
